/**
 * ordenes-compra.ts
 * Compras (REQUI) module — Órdenes de Compra section: case-scoped and
 * PO-centric purchase order summaries/detail, PO documents, the global PO
 * listing, and two-tier PO approval (threshold + pending approvals).
 *
 * refactor/compras-by-section — pure move out of index.ts (no shape/behavior change).
 */
import { z } from 'zod';

import { QuotationAttachmentResponseSchema } from './cotizaciones.js';

/**
 * GET /procurement/cases/:id/purchase-orders (REQ-27) — case-scoped, read-only
 * PO summary grouped round → supplier/PO → lines. A PO can hold lines from
 * several rounds (one PO per case+supplier — "agrupación de OCs"), so grouping
 * is per-line: a PO appears under EACH contributing round showing only that
 * round's lines. `roundId`/`roundNo` null is a defensive bucket (see design.md
 * D3) — unreachable in practice since quote_lines.round_id is NOT NULL.
 */
export const CasePurchaseOrderLineSchema = z.object({
  itemId: z.string().uuid(),
  lineNo: z.number(),
  description: z.string(),
  qty: z.string(),
  /** Net of VAT, always. */
  unitPrice: z.string(),
  /** VAT rate frozen when the order was emitted (migration 0015), a percentage — e.g. "21.00". */
  ivaRate: z.string(),
  qtyReceived: z.string(),
  annulled: z.boolean(),
  /** OC detail screen (mockup 2026-08-23, decisión 15) — null para ítems de texto libre sin item_master. */
  itemCode: z.string().nullable(),
  unit: z.string(),
});
export type CasePurchaseOrderLine = z.infer<typeof CasePurchaseOrderLineSchema>;

export const CasePurchaseOrderSummarySchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  status: z.string(),
  total: z.string(),
  currency: z.string(),
  emittedAt: z.string(),
  /** Blueprint §6: la tarjeta se rotula "Contrato" y muestra período desde/hasta. */
  isRentalContract: z.boolean(),
  rentalStart: z.string().nullable(),
  rentalEnd: z.string().nullable(),
  /** Blueprint §6: "N° de comprobante y observación editables inline, como en el legacy". */
  receiptNumber: z.string().nullable(),
  observation: z.string().nullable(),
  /** OC detail screen (mockup 2026-08-23, decisión 2/16) — migración 0149. */
  sentAt: z.string().nullable(),
  updatedAt: z.string(),
  /**
   * Wizard paso ④ mockup (vista_oc_wizard.png, 2026-08-24) — migración 0150.
   * `z.string()` suelto, no el enum — mismo criterio que `status` arriba: el
   * valor crudo de la columna se pasa tal cual en la lectura, la validación
   * estricta del enum vive en el input de escritura (ActualizarOrdenCompraEventSchema).
   */
  paymentMethod: z.string().nullable(),
  /** Derivada (decisión 6) — max(emittedAt + deliveryDays de las quotations de origen), no persistida, no editable. */
  deliveryDate: z.string().nullable(),
  /** Resumen legible de purchase_order_payment_term (decisión 14) — null si la OC no tiene cronograma cargado. */
  paymentTermsSummary: z.string().nullable(),
  lines: z.array(CasePurchaseOrderLineSchema),
});
export type CasePurchaseOrderSummary = z.infer<typeof CasePurchaseOrderSummarySchema>;

/** GET /procurement/purchase-orders/:id/documents — tab "Documentos" (decisión 7): adjuntos de las cotizaciones de origen. */
export const PurchaseOrderDocumentsResponseSchema = z.object({
  documents: z.array(QuotationAttachmentResponseSchema),
});
export type PurchaseOrderDocumentsResponse = z.infer<typeof PurchaseOrderDocumentsResponseSchema>;

export const CasePurchaseOrderRoundGroupSchema = z.object({
  roundId: z.string().uuid().nullable(),
  roundNo: z.number().nullable(),
  orders: z.array(CasePurchaseOrderSummarySchema),
});
export type CasePurchaseOrderRoundGroup = z.infer<typeof CasePurchaseOrderRoundGroupSchema>;

export const CasePurchaseOrdersResponseSchema = z.object({
  groups: z.array(CasePurchaseOrderRoundGroupSchema),
});
export type CasePurchaseOrdersResponse = z.infer<typeof CasePurchaseOrdersResponseSchema>;

// ---------------------------------------------------------------------------
// requi-cotizacion-consolidada-por-rubro (Slice B, task 2.12, design.md D7)
// — GET /procurement/purchase-orders/:id, PO-centric detail (not keyed by
// any case id). Works for both a case-owned (single-SA) and a round-owned
// (consolidated) purchase order.
// ---------------------------------------------------------------------------

/** Per-line SA attribution, added to CasePurchaseOrderLineSchema's fields. */
export const PurchaseOrderDetailLineSchema = CasePurchaseOrderLineSchema.extend({
  caseId: z.string().uuid(),
  itemMasterId: z.string().uuid().nullable(),
});
export type PurchaseOrderDetailLine = z.infer<typeof PurchaseOrderDetailLineSchema>;

export const PurchaseOrderOwnerSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('case'), caseId: z.string().uuid() }),
  z.object({
    kind: z.literal('round'),
    roundId: z.string().uuid(),
    consolidationNo: z.number(),
    categoryId: z.string().uuid().nullable(),
    categoryName: z.string().nullable(),
  }),
]);
export type PurchaseOrderOwner = z.infer<typeof PurchaseOrderOwnerSchema>;

export const PurchaseOrderDetailCaseSchema = z.object({
  caseId: z.string().uuid(),
  code: z.string(),
  title: z.string(),
  requesterName: z.string().nullable(),
  costCenterName: z.string().nullable(),
  /** The SA's Área / Yacimiento — derived from the SA, like costCenterName. */
  areaName: z.string().nullable(),
  deliveryLocationId: z.string().uuid().nullable(),
  deliveryLocationName: z.string().nullable(),
  /** catalog.delivery_location.address (migration 0015). */
  deliveryLocationAddress: z.string().nullable(),
  subtotal: z.string(),
});
export type PurchaseOrderDetailCase = z.infer<typeof PurchaseOrderDetailCaseSchema>;

export const PurchaseOrderDetailResponseSchema = CasePurchaseOrderSummarySchema.omit({ lines: true }).extend({
  owner: PurchaseOrderOwnerSchema,
  cases: z.array(PurchaseOrderDetailCaseSchema),
  lines: z.array(PurchaseOrderDetailLineSchema),
  /** Who emitted the order — null when its timeline has no emission event. */
  emittedByName: z.string().nullable(),
});
export type PurchaseOrderDetailResponse = z.infer<typeof PurchaseOrderDetailResponseSchema>;

/**
 * fix/rental-demo ÍTEM 1 — GET /procurement/purchase-orders, the full-history
 * global PO listing (unlike DashboardOpenOrderRow, no status exclusion — every
 * PO, rechazada_proveedor included). Alcance mínimo: only the 5 columns asked
 * for, no buyer/costCenter/deliveryLocation/category resolution.
 */
export const PurchaseOrderListRowSchema = z.object({
  poId: z.string().uuid(),
  code: z.string(),
  /**
   * The owning purchase_case's id. `null` for a consolidated (round-owned)
   * OC since migration 0203 (requi-cotizacion-consolidada-por-rubro, Slice
   * B, design.md D6) — see `roundId` below, set exactly when this is null.
   * The frontend navigates by `poId` alone (PO-centric route), not by this
   * field, since this change.
   */
  caseId: z.string().uuid().nullable(),
  /** Set exactly when `caseId` is null — the owning consolidation round's id. */
  roundId: z.string().uuid().nullable(),
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  status: z.string(),
  statusLabel: z.string(),
  total: z.string(),
  currency: z.string(),
  emittedAt: z.string(),
});
export type PurchaseOrderListRow = z.infer<typeof PurchaseOrderListRowSchema>;

export const PurchaseOrderListPageSchema = z.object({
  items: z.array(PurchaseOrderListRowSchema),
  totalCount: z.number(),
  totalPages: z.number(),
  page: z.number(),
  pageSize: z.number(),
});
export type PurchaseOrderListPage = z.infer<typeof PurchaseOrderListPageSchema>;

// ---------------------------------------------------------------------------
// 2026-08-31-requi-aprobacion-oc-por-monto — two-tier PO approval, Phase 1
// (contracts only; repository/use case/route wiring is Phase 1/3/4).
// ---------------------------------------------------------------------------

/**
 * compras.company_settings.po_approval_threshold (design.md D4). `null`
 * means tier 2 never applies for that company — tier 1 has no equivalent
 * config and can never be disabled this way (design.md D4/PAT-1).
 * `.positive()` — a threshold of 0 would make every PO escalate, which is
 * indistinguishable from "no threshold configured" done backwards; clearing
 * it is `null`, not `0`.
 */
export const PoApprovalThresholdSchema = z.object({
  threshold: z.number().positive().nullable(),
});
export type PoApprovalThreshold = z.infer<typeof PoApprovalThresholdSchema>;

/**
 * compras.company_settings.po_terms (migration 0015) — the tenant's standing
 * clauses printed at the foot of every purchase order (invoicing
 * instructions, contractor access rules...). Blank or `null` clears them.
 */
export const PoTermsSchema = z.object({
  poTerms: z
    .string()
    .max(4000)
    .nullable()
    .transform((value) => (value === null || value.trim() === '' ? null : value.trim())),
});
export type PoTerms = z.infer<typeof PoTermsSchema>;

/**
 * GET /procurement/approvals/pending-purchase-orders row shape (design.md
 * D7) — derived (not stored) from case_items.status joined to
 * quote_lines.status = 'adjudicada'. Deliberately NOT merged into
 * PurchaseCaseListRow (a different row shape keyed by purchase_cases.status,
 * design.md D7 "Widening the shipped contract into a union is rejected").
 */
export const PendingPurchaseOrderRowSchema = z.object({
  /** Nullable exactly when `roundId` is set (design.md D10) — a case-owned group's owner. */
  caseId: z.string().uuid().nullable(),
  caseCode: z.string().nullable(),
  /** Set exactly when this group is owned by a consolidation round (design.md D10). */
  roundId: z.string().uuid().nullable(),
  consolidationNo: z.number().nullable(),
  /** Every distinct affected SA for a round-owned group; a single entry for a case-owned group. */
  cases: z.array(z.object({ caseId: z.string().uuid(), caseCode: z.string() })),
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  total: z.string(),
  currency: z.string(),
  tier: z.enum(['n1', 'n2']),
  itemIds: z.array(z.string().uuid()),
  /**
   * requi-consolidado-oc-ux (T3, 2026-09-26) — the actorId of the persisted
   * `aprobar_oc_n1` case_event for this exact (owner, supplierId) group; only
   * ever populated for a tier 'n2' row (mirrors ApprovePoTierUseCase's own
   * `tier1ApprovedByActor` derivation, errors.ts's `SameApproverError` header
   * comment — same event, same lookup, this time read-side). `null` for a
   * tier 'n1' row, or when no matching tier-1 approval event is found yet.
   * A read-side field only — no new domain transition or persisted column.
   */
  tier1ApproverId: z.string().uuid().nullable(),
  /**
   * Names of the active users who can give THIS tier's approval (tier 'n2'
   * excludes whoever approved tier 1). Empty means nobody can: the group is
   * stuck until someone is given the capability. Optional for old payloads.
   */
  eligibleApprovers: z.array(z.string()).optional(),
});
export type PendingPurchaseOrderRow = z.infer<typeof PendingPurchaseOrderRowSchema>;

/**
 * GET /procurement/approvals/pending-purchase-orders/detail?caseId|roundId&supplierId&tier — WHAT an approver approves
 * for one row of the inbox above: the same group (same grouping, same total), with its SAs, lines and prices, and why
 * this supplier (the best alternative per line in the same round, the deviation reason when it was not the cheapest).
 * Read-only; 404 when the group is no longer pending (approved/rejected meanwhile).
 */
export const PendingPoApprovalDetailQuerySchema = z
  .object({
    caseId: z.string().uuid().optional(),
    roundId: z.string().uuid().optional(),
    supplierId: z.string().uuid(),
    tier: z.enum(['n1', 'n2']),
  })
  .refine((q) => (q.caseId === undefined) !== (q.roundId === undefined), { message: 'Exactly one of caseId / roundId.' });
export type PendingPoApprovalDetailQuery = z.infer<typeof PendingPoApprovalDetailQuerySchema>;

export const PendingPoApprovalLineSchema = z.object({
  itemId: z.string().uuid(),
  caseCode: z.string(),
  lineNo: z.number().int(),
  description: z.string(),
  qty: z.string(),
  unit: z.string(),
  unitPrice: z.string(),
  subtotal: z.string(),
  /** DEVIATION_REASONS code recorded when this supplier was not the suggested one; `null` otherwise. */
  deviationReason: z.string().nullable(),
  /** The cheapest OTHER offer for this line in the same round and currency, only when it is cheaper; `null` otherwise. */
  mejorAlternativa: z.object({ supplierName: z.string(), unitPrice: z.string() }).nullable(),
});
export type PendingPoApprovalLine = z.infer<typeof PendingPoApprovalLineSchema>;

export const PendingPoApprovalDetailSchema = z.object({
  caseId: z.string().uuid().nullable(),
  roundId: z.string().uuid().nullable(),
  consolidationNo: z.number().nullable(),
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  currency: z.string(),
  total: z.string(),
  tier: z.enum(['n1', 'n2']),
  /** After this approval Gerencia still has to approve (tier 'n1' over the amount threshold), or this IS Gerencia's. */
  requiereGerencia: z.boolean(),
  /** Who approved tier 1 (tier 'n2' only) — the id guards "the same person cannot approve both tiers". */
  tier1ApproverId: z.string().uuid().nullable(),
  tier1ApproverName: z.string().nullable(),
  paymentTerms: z.string().nullable(),
  deliveryDays: z.number().nullable(),
  cases: z.array(
    z.object({
      caseId: z.string().uuid(),
      caseCode: z.string(),
      title: z.string(),
      requesterName: z.string().nullable(),
      costCenterName: z.string().nullable(),
    }),
  ),
  lines: z.array(PendingPoApprovalLineSchema),
  /** Suppliers that quoted at least one of these lines (this one included). */
  proveedoresCotizantes: z.number().int(),
  /** Every line is at the best price among the offers. */
  esLaMasBarata: z.boolean(),
  /** Σ (unitPrice − best alternative) × qty over the lines that were not the cheapest; "0.00" when it is the cheapest. */
  diferenciaVsMejor: z.string(),
});
export type PendingPoApprovalDetail = z.infer<typeof PendingPoApprovalDetailSchema>;
